'use client';

import { useEffect, useState, useCallback, useMemo } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { CheckCircle2, AlertTriangle, Circle, Loader2, BookOpen } from 'lucide-react';
import { Progress } from '@/shared/components/ui/progress';
import { Button } from '@/shared/components/ui/button';
import { useContentPlan } from '@/contexts/content/hooks/useContentPlan';
import { addEventSourceListener } from '@/shared/hooks/useEventSource';
import { useFunMessage } from '@/shared/hooks/useFunMessage';
import { useWorkflowRun, type WorkflowRunStatus } from '@/shared/hooks/useWorkflowRun';
import type { TaskType } from '@/contexts/content/types/enums';
import { useOrderByOrderId, useOrderLayers } from '@/contexts/pipeline-v2/hooks/useOrderLayers';
import { OrderLayerTiles, stepLabelKey } from '@/contexts/pipeline-v2/components/OrderLayerTiles';
import { orderLayer, orderOutcome, orderProgress } from '@/contexts/pipeline-v2/order/view';
import type { OrderLayersView } from '@/contexts/pipeline-v2/order/types';

/** story-package engine step per task type (storyPackageWorkflow.ts). */
const TASK_TYPE_STEP: Partial<Record<TaskType, string>> = {
  generate_story: 'story',
  generate_audio_story: 'audiobook',
  create_book: 'book',
};

/**
 * Honest per-task status from the plan's story-package run: steps before the
 * cursor are done, the cursor step is running, the rest wait. The legacy plan
 * tasks mark e.g. audio 'completed' the moment the SHELL exists — the run
 * reflects the actual generation.
 */
function engineTaskStatus(
  run: WorkflowRunStatus,
  taskType: TaskType,
): string | null {
  const stepId = TASK_TYPE_STEP[taskType];
  if (!stepId) return null;
  const order = run.steps.map((s) => s.stepId);
  // generate_story spans 'story-gen' (engine text child, when present) AND
  // the 'story' projection/ack step — the task is done once BOTH are past.
  const span = (
    stepId === 'story'
      ? ['story-gen', 'story']
      : [stepId, 'media']  // audio/book may live inside the parallel media step
  )
    .map((id) => order.indexOf(id))
    .filter((i) => i !== -1);
  if (span.length === 0) return null;
  const first = Math.min(...span);
  const last = Math.max(...span);
  if (run.status === 'completed') return 'completed';
  const cursorIdx = run.currentStep ? order.indexOf(run.currentStep) : -1;
  if (run.status === 'failed') {
    return last < cursorIdx ? 'completed' : cursorIdx >= first ? 'failed' : 'pending';
  }
  if (last < cursorIdx) return 'completed';
  if (cursorIdx >= first && cursorIdx <= last) return 'in_progress';
  return 'pending';
}

const taskTypeKeys: Record<TaskType, string> = {
  generate_story: 'generate_story',
  generate_audio_story: 'generate_audio_story',
  create_book: 'create_book',
  translate_story: 'translate_story',
};

/** Display order: story first, then audio, then book */
const TASK_TYPE_ORDER: Record<TaskType, number> = {
  generate_story: 0,
  translate_story: 0,
  generate_audio_story: 1,
  create_book: 2,
};

const stageKeys: Record<string, string> = {
  'story:outline': 'stageOutline',
  'story:generating': 'stageGenerating',
  'story:reviewing': 'stageReviewing',
  'story:revising': 'stageRevising',
  'story:done': 'stageDone',
  'story:completed': 'stageDone',
};

const FUN_MESSAGE_COUNT = 10;

function useGenerationFunMessage(active: boolean, t: ReturnType<typeof useTranslations>) {
  const messages = useMemo(
    () => Array.from({ length: FUN_MESSAGE_COUNT }, (_, i) => t(`funMessages.default.${i}`)),
    [t],
  );

  return useFunMessage(messages, active);
}

function TaskStatusIcon({ status }: { status: string }) {
  switch (status) {
    case 'completed':
      return <CheckCircle2 className="size-5 shrink-0 text-success" />;
    case 'failed':
      return <AlertTriangle className="size-5 shrink-0 text-destructive" />;
    case 'in_progress':
      return <Loader2 className="size-5 shrink-0 animate-spin text-primary" />;
    default:
      return <Circle className="size-5 shrink-0 text-muted-foreground/40" />;
  }
}

/**
 * After the wizard: `?plan=<id>` (startContentCreation returns the v2 order id
 * as planId) or `?story=<id>`. A v2 order shows its layers (states + step
 * labels) and hands over to the story as soon as the text is there; an id that
 * is not a v2 order is an old v1 plan and keeps the v1 screen.
 */
export function GeneratingScreen() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const locale = useLocale();
  const t = useTranslations('stories.workspace.planProgress');

  const planId = searchParams.get('plan');
  const storyParam = searchParams.get('story');
  const byOrder = useOrderByOrderId(storyParam ? null : planId);
  const storyId = storyParam ?? byOrder.data?.storyId ?? null;
  const { data: storyOrder } = useOrderLayers(storyId);
  const order = storyOrder ?? byOrder.data?.view ?? null;

  if (!planId && !storyParam) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <p className="text-muted-foreground">{t('failed')}</p>
        <Button onClick={() => router.push(`/${locale}/stories`)}>
          {t('dismiss')}
        </Button>
      </div>
    );
  }

  if (order && storyId) return <GeneratingScreenV2 order={order} storyId={storyId} />;

  // still resolving whether this is a v2 order
  const resolving = storyParam ? storyOrder === undefined : byOrder.data === undefined && !byOrder.isError;
  if (resolving) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center" data-testid="generating-resolving">
        <Loader2 className="size-8 animate-spin text-primary/60" />
      </div>
    );
  }
  // a story without a v2 order (or the order of an id that is not v2): v1
  if (storyParam && !planId) {
    return <GoToStory storyId={storyParam} />;
  }
  return <GeneratingScreenV1 planId={planId!} />;
}

function GoToStory({ storyId }: { storyId: string }) {
  const router = useRouter();
  const locale = useLocale();
  useEffect(() => { router.replace(`/${locale}/stories/${storyId}`); }, [router, locale, storyId]);
  return null;
}

/**
 * The v2 order on its way: every layer as a tile with its state and step, one
 * bar, and the hand-over to the story the moment the text exists — the rest of
 * the layers keep going on the story page (the same tiles, live).
 */
export function GeneratingScreenV2({ order, storyId }: { order: OrderLayersView; storyId: string }) {
  const router = useRouter();
  const locale = useLocale();
  const t = useTranslations('stories.workspace.planProgress');
  const tl = useTranslations('stories.orderLayers');
  const story = orderLayer(order, 'story');
  const outcome = orderOutcome(order);
  const storyFailed = story?.state === 'failed' || story?.state === 'cancelled' || outcome === 'failed' || outcome === 'cancelled';
  const textReady = story?.state === 'ready' || story?.state === 'partial';
  const finalOk = outcome === 'ready' || outcome === 'partial';
  const active = !storyFailed && !textReady && !finalOk;
  const funMessage = useGenerationFunMessage(active, t);

  useEffect(() => {
    if (storyFailed) return;
    if (textReady || finalOk) router.replace(`/${locale}/stories/${storyId}`);
  }, [storyFailed, textReady, finalOk, router, locale, storyId]);

  const stepKey = story ? stepLabelKey(story) : null;
  const stepLabel = stepKey && tl.has(stepKey) ? tl(stepKey) : null;
  const progress = Math.max(orderProgress(order), active ? 5 : 0);

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-4" data-testid="generating-v2" data-outcome={outcome ?? ''}>
      <div className="w-full max-w-md space-y-8">
        <div className="flex justify-center">
          {storyFailed
            ? <AlertTriangle className="size-16 text-destructive/60" />
            : <BookOpen className="size-16 text-primary/60" />}
        </div>
        <div className="text-center space-y-1">
          <h1 className="text-lg font-semibold" data-testid="generating-title">
            {storyFailed ? t('failed') : finalOk || textReady ? t('completed') : t('title')}
          </h1>
          {stepLabel && !storyFailed && (
            <p className="text-sm text-muted-foreground" data-testid="generating-step">{stepLabel}</p>
          )}
        </div>
        {active && (
          <div className="space-y-3">
            <Progress value={progress} className="h-2" />
            <p className="text-center text-sm text-muted-foreground italic transition-opacity duration-500">{funMessage}</p>
          </div>
        )}
        <OrderLayerTiles order={order} />
        {storyFailed && (
          <div className="flex justify-center">
            <Button variant="outline" onClick={() => router.push(`/${locale}/stories`)} data-testid="generating-dismiss">
              {t('dismiss')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

/** The v1 content plan screen — only for a plan id that is not a v2 order (old links). */
function GeneratingScreenV1({ planId }: { planId: string }) {
  const router = useRouter();
  const locale = useLocale();
  const t = useTranslations('stories.workspace.planProgress');

  const { data: plan } = useContentPlan(planId);

  // PLAN_ENGINE=workflow: the plan's story-package run is the honest progress
  // source (startKey 'plan-<planId>'). Legacy plans have no run — 404 →
  // pkgRun stays undefined and the task statuses below fall back to the saga.
  const pkgRunState = useWorkflowRun(planId ? { startKey: `plan-${planId}` } : null);
  const pkgRun: WorkflowRunStatus | null = pkgRunState.runId && pkgRunState.status
    ? {
        runId: pkgRunState.runId,
        workflow: 'story-package',
        status: pkgRunState.status,
        currentStep: pkgRunState.currentStep,
        error: pkgRunState.error,
        steps: pkgRunState.steps,
        startedAt: '',
        updatedAt: '',
      }
    : null;
  // Audiobook child run ('{parentRunId}:audiobook') carries per-block progress.
  const audioChildRun = useWorkflowRun(
    pkgRun ? { startKey: `${pkgRun.runId}:audiobook` } : null,
  );
  const audioChildStep = audioChildRun.steps.find((s) => s.stepId === 'tts-blocks');

  const [currentStage, setCurrentStage] = useState<string | null>(null);
  const [stageProgress, setStageProgress] = useState(0);

  // Find this plan's story task — used to filter SSE events by storyId
  const storyTask = plan?.tasks.find(
    (task) => (task.type === 'generate_story' || task.type === 'translate_story')
      && (task.status === 'in_progress' || task.status === 'pending'),
  );
  const planStoryId = storyTask?.entityId ?? plan?.sourceEntityId;
  const handleProgressEvent = useCallback(
    (payload: Record<string, unknown>) => {
      // Filter: only accept events for THIS plan's story
      // jobId in SSE maps to PipelineJob which has a storyId
      // We can't filter by jobId directly (not available on client),
      // but we CAN filter by storyId if the event includes it
      if (planStoryId && payload.storyId && payload.storyId !== planStoryId) {
        return; // Event for a different story — ignore
      }

      const stage = payload.stage as string | undefined;
      if (stage) setCurrentStage(stage);
      if (typeof payload.progress === 'number') setStageProgress(payload.progress);
    },
    [planStoryId],
  );

  // Listen for SSE progress as soon as we have a story task (even if pending — events may arrive before refetch)
  const hasStoryTask = !!storyTask;

  useEffect(() => {
    if (!hasStoryTask) return;
    return addEventSourceListener('stories.progress', handleProgressEvent);
  }, [hasStoryTask, handleProgressEvent]);

  // Redirect when plan completes
  useEffect(() => {
    if (!plan) return;

    // Package mode: leave for the story editor AS SOON AS the text exists —
    // audio/book keep generating and their statuses live on the story page.
    // Holding the user here for the whole run (~a minute on the throttled
    // dev stand) reads as "stuck". The engine run tells us precisely when
    // the story step is passed.
    if (pkgRun && pkgRun.status === 'running') {
      const order = pkgRun.steps.map((s) => s.stepId);
      const cursorIdx = pkgRun.currentStep ? order.indexOf(pkgRun.currentStep) : -1;
      const storyIdx = order.indexOf('story');
      const textReady = storyIdx !== -1 && cursorIdx > storyIdx;
      if (!textReady) return;
    }

    const planDone = plan.status === 'completed' || plan.status === 'completed_with_errors';
    // Engine mode reaches here only when the text is ready — that alone is
    // enough to hand over to the story editor.
    if (planDone || pkgRun) {
      // Find storyId from generate_story task or translate_story task
      const storyTask = plan.tasks.find(
        (t) => (t.type === 'generate_story' || t.type === 'translate_story') && t.entityId,
      );
      const storyId = storyTask?.entityId ?? plan.sourceEntityId;

      if (storyId) {
        // Build URL params for auto-selecting created entities
        const params = new URLSearchParams();
        const bookTask = plan.tasks.find((t) => t.type === 'create_book' && t.entityId);
        const audioTask = plan.tasks.find((t) => t.type === 'generate_audio_story' && t.entityId);
        if (bookTask?.entityId) params.set('book', bookTask.entityId);
        if (audioTask?.entityId) params.set('audio', audioTask.entityId);
        const qs = params.toString();
        router.replace(`/${locale}/stories/${storyId}${qs ? `?${qs}` : ''}`);
      }
    }
  }, [plan, pkgRun, router, locale]);

  // Compute progress — blend task-level and stage-level progress
  const completedCount = plan?.tasks.filter((task) => task.status === 'completed').length ?? 0;
  const inProgressCount = plan?.tasks.filter((task) => task.status === 'in_progress').length ?? 0;
  const totalCount = plan?.tasks.length ?? 1;
  // Each completed task = full slice, in-progress task = partial slice from stageProgress
  const taskSlice = 100 / totalCount;
  const overallProgress = Math.round(
    completedCount * taskSlice +
    (inProgressCount > 0 ? (stageProgress / 100) * taskSlice : 0)
  );
  // Package mode: progress from the engine run — equal step slices, the
  // audiobook slice refined by child item counts, the story slice by the
  // legacy stage % (stories.progress SSE).
  let engineProgress: number | null = null;
  if (pkgRun) {
    const order = pkgRun.steps.map((s) => s.stepId);
    const stepCount = order.length || 1;
    if (pkgRun.status === 'completed') {
      engineProgress = 100;
    } else {
      const cursor = pkgRun.currentStep ? order.indexOf(pkgRun.currentStep) : -1;
      const doneSteps = Math.max(cursor, 0);
      let stepFraction = 0;
      if (pkgRun.currentStep === 'audiobook' && audioChildStep?.total) {
        stepFraction = (audioChildStep.done ?? 0) / audioChildStep.total;
      } else if (pkgRun.currentStep === 'story') {
        stepFraction = stageProgress / 100;
      }
      engineProgress = Math.round(((doneSteps + stepFraction) / stepCount) * 100);
    }
  }

  // Show at least 5% when plan is executing so user sees movement
  const rawProgress = engineProgress ?? overallProgress;
  const displayProgress = plan?.status === 'executing' && rawProgress === 0 ? 5 : rawProgress;

  const showStageDetail = !!currentStage;
  const stageKey = currentStage ? stageKeys[currentStage] : null;

  const isFailed = plan?.status === 'failed' || pkgRun?.status === 'failed';
  const isPartial = plan?.status === 'completed_with_errors';
  const isActive = pkgRun
    ? pkgRun.status === 'running'
    : !isFailed && !isPartial && plan?.status !== 'completed';
  const funMessage = useGenerationFunMessage(isActive ?? false, t);

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-4">
      <div className="w-full max-w-md space-y-8">
        {/* Icon */}
        <div className="flex justify-center">
          {isFailed ? (
            <AlertTriangle className="size-16 text-destructive/60" />
          ) : (
            <BookOpen className="size-16 text-primary/60" />
          )}
        </div>

        {/* Title */}
        <div className="text-center space-y-1">
          <h1 className="text-lg font-semibold">
            {isFailed ? t('failed') : plan?.status === 'completed' ? t('completed') : t('title')}
          </h1>
          {showStageDetail && stageKey && (
            <p className="text-sm text-muted-foreground">
              {t(stageKey)}
            </p>
          )}
        </div>

        {/* Progress bar */}
        {isActive && (
          <div className="space-y-3">
            <Progress
              value={displayProgress}
              className="h-2"
            />
            <p className="text-center text-sm text-muted-foreground italic transition-opacity duration-500">
              {funMessage}
            </p>
          </div>
        )}

        {/* Task list */}
        {plan && (
          <div className="space-y-3">
            {[...plan.tasks].sort((a, b) => (TASK_TYPE_ORDER[a.type] ?? 9) - (TASK_TYPE_ORDER[b.type] ?? 9)).map((task) => {
              const engineStatus = pkgRun ? engineTaskStatus(pkgRun, task.type) : null;
              const status = engineStatus ?? task.status;
              const showAudioProgress =
                task.type === 'generate_audio_story'
                && status === 'in_progress'
                && !!audioChildStep?.total;
              return (
                <div key={task.id} className="flex items-center gap-3">
                  <TaskStatusIcon status={status} />
                  <span className="text-sm">
                    {t(`taskTypes.${taskTypeKeys[task.type]}`)}
                    {showAudioProgress && (
                      <span className="text-muted-foreground">
                        {` ${audioChildStep?.done ?? 0}/${audioChildStep?.total}`}
                      </span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* Failed — retry / go back */}
        {isFailed && (
          <div className="flex justify-center">
            <Button
              variant="outline"
              onClick={() => router.push(`/${locale}/stories`)}
            >
              {t('dismiss')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
